import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { FileText, FileImage, Calendar, Store, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const WarrantyModal = ({ open, onOpenChange, item }) => {
    if (!item) return null;

    const handleViewDocument = () => {
        if (item.warrantyDocUrl) {
            window.open(item.warrantyDocUrl, "_blank", "noopener,noreferrer");
        }
    };

    const cleanUrl = item.warrantyDocUrl ? item.warrantyDocUrl.split('?')[0] : '';
    const isImageUrl = cleanUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(cleanUrl);
    const hasDocument = !!item.warrantyDocUrl;

    const formatDate = (dateString) => {
        if (!dateString) return null;
        try {
            return new Date(dateString).toLocaleDateString("en-ZA", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        } catch {
            return dateString;
        }
    };

    const isWarrantyExpired = () => {
        if (!item.warrantyExpiry) return false;
        return new Date(item.warrantyExpiry) < new Date();
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-sm mx-4 rounded-3xl w-[95vw] max-w-[95vw] overflow-hidden">
                <DialogHeader>
                    <DialogTitle className="text-lg font-semibold">{item.name}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 pt-2">
                    {/* Document Preview */}
                    {hasDocument ? (
                        <div className="space-y-3">
                            {isImageUrl ? (
                                <div className="rounded-2xl overflow-hidden border border-border">
                                    <img
                                        src={item.warrantyDocUrl}
                                        alt="Warranty Document"
                                        className="w-full h-40 object-cover"
                                    />
                                </div>
                            ) : (
                                <div className="bg-muted/30 rounded-2xl p-6 flex flex-col items-center justify-center min-h-[120px] border border-border/50">
                                    <FileText className="w-10 h-10 text-muted-foreground mb-2" />
                                    <p className="text-sm text-muted-foreground">PDF Document</p>
                                </div>
                            )}
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full rounded-xl"
                                onClick={handleViewDocument}
                            >
                                <ExternalLink className="w-4 h-4 mr-2" />
                                View Document
                            </Button>
                        </div>
                    ) : (
                        <div className="bg-muted/30 rounded-2xl p-6 flex flex-col items-center justify-center min-h-[120px] border border-border/50">
                            <FileImage className="w-10 h-10 text-muted-foreground/50 mb-2" />
                            <p className="text-sm text-muted-foreground">No warranty document uploaded</p>
                        </div>
                    )}

                    {/* Details */}
                    <div className="space-y-3">
                        {item.merchant && (
                            <div className="flex items-center gap-3">
                                <Store className="w-4 h-4 text-muted-foreground shrink-0" />
                                <div>
                                    <p className="text-xs text-muted-foreground">Brand</p>
                                    <p className="text-sm font-medium">{item.merchant}</p>
                                </div>
                            </div>
                        )}
                        {item.purchaseDate && (
                            <div className="flex items-center gap-3">
                                <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                                <div>
                                    <p className="text-xs text-muted-foreground">Installation Date</p>
                                    <p className="text-sm font-medium">{formatDate(item.purchaseDate)}</p>
                                </div>
                            </div>
                        )}
                        {item.warrantyExpiry && (
                            <div className="flex items-center gap-3">
                                <Calendar className={`w-4 h-4 shrink-0 ${isWarrantyExpired() ? "text-destructive" : "text-primary"}`} />
                                <div>
                                    <p className="text-xs text-muted-foreground">Warranty Until</p>
                                    <p className={`text-sm font-medium ${isWarrantyExpired() ? "text-destructive" : "text-primary"}`}>
                                        {formatDate(item.warrantyExpiry)}
                                        {isWarrantyExpired() && " (Expired)"}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default WarrantyModal;
